
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ProfileCompletionCard } from "@/components/nursing-homes/owner/dashboard/ProfileCompletionCard";
import { ProfileTabs } from "@/components/nursing-homes/owner/dashboard/profile/ProfileTabs";

type ContextType = { nursingHome: NursingHome };

export default function ProfilePage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const handleSave = async (formData: Partial<NursingHome>) => {
    try {
      setIsSaving(true);
      
      // Create a clean object for the update that matches the DB schema
      const updateData: Record<string, any> = {};
      
      // Copy the formData but handle deposit_amount conversion
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'deposit_amount' && typeof value === 'string' && value) {
          updateData[key] = parseFloat(value);
        } else {
          updateData[key] = value;
        }
      });
      
      const { data, error } = await supabase
        .from('nursing_homes')
        .update(updateData)
        .eq('nh_slug', nursingHome.nh_slug)
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success("Profile updated successfully");
      return data as NursingHome;
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Profile Management</h1>
          <p className="text-muted-foreground">
            Update your nursing home's profile information
          </p>
        </div>
        <ProfileCompletionCard nursingHome={nursingHome} />
      </div>
      
      <ProfileTabs 
        nursingHome={nursingHome} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  );
}
